import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Share2, Edit, X } from 'lucide-react'
import { Deck, Card } from '../types'
import { useAppStore } from '../store'
import { CardImage } from '../components/ui/CardImage'

function getDeckCards(deck: Deck, allCards: Card[]): Card[] {
  const cards: Card[] = []
  deck.cards.forEach(deckCard => {
    const card = allCards.find(c => c.id === deckCard.cardId || c.cardId === deckCard.cardId)
    if (card) {
      for (let i = 0; i < deckCard.quantity; i++) {
        cards.push(card)
      }
    }
  })
  return cards
}

function createRandomDecks(cards: Card[]): Deck[] {
  if (cards.length === 0) return []

  const STORAGE_KEY = 'demo-decks-v1'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch (e) {
      console.error('Error loading demo decks:', e)
    }
  }

  const formats = ['RACIAL', 'RACIAL_LIBRE', 'RACIAL_LIMITADO', 'RACIAL_EDICION', 'VCR', 'TRIADAS'] as const
  const factions = Object.values({ HUMAN: 'HUMAN', DEMON: 'DEMON', ELF: 'ELF', DRAGON: 'DRAGON', UNDEAD: 'UNDEAD', NEUTRAL: 'NEUTRAL' })

  const cardsWithImage = cards.filter(card => {
    const cardAny = card as any
    return cardAny.localImage || card.imageUrl
  })

  console.log('Cartas con imagen:', cardsWithImage.length, 'de', cards.length)

  const goldCards = cardsWithImage.filter(card => card.goldCost !== undefined && card.goldCost > 0)
  const otherCards = cardsWithImage.filter(card => card.goldCost === undefined || card.goldCost === 0)

  console.log('Cartas de oro con imagen:', goldCards.length)
  console.log('Otras cartas con imagen:', otherCards.length)

  if (goldCards.length === 0) {
    console.log('ADVERTENCIA: No hay cartas de oro con imagen.')
    return []
  }

  const deckNames = [
    'Ejército de Caballeros',
    'Dracónido Infinito',
    'Sombra Eterna',
    'Guardianes del Bosque',
    'Liga de Demonios',
    'Fuerza Neutral',
    'Campeones Imperio',
    'Maestros Furia',
  ]

  const deckDescriptions = [
    'Mazo agresivo con énfasis en ataques rápidos',
    'Estrategia de control con dragones poderosos',
    'Mazo de no-muertos con regeneración',
    'Elfos y criaturas mágicas para ganar por ventaja',
    'Demonios con efectos de daño masivo',
    'Equilibrio perfecto con cartas neutrales',
    'Combinación racial para meta competitivo',
    'Mazo experimental con triadas',
  ]

  const decks = deckNames.slice(0, 8).map((name, i) => {
    const faction = factions[i % factions.length]

    const factionGoldCards = goldCards.filter(c => c.faction === faction)
    const factionOtherCards = otherCards.filter(c => c.faction === faction)

    let selectedGoldCards = factionGoldCards.slice(0, 18)

    if (selectedGoldCards.length < 15) {
      const additionalGold = goldCards.filter(c => c.faction !== faction)
      selectedGoldCards = [...selectedGoldCards, ...additionalGold.slice(0, 15 - selectedGoldCards.length)]
    }

    let selectedOtherCards = factionOtherCards.slice(0, 50 - selectedGoldCards.length)

    if (selectedOtherCards.length < 50 - selectedGoldCards.length) {
      const additional = otherCards.filter(c => c.faction !== faction)
      selectedOtherCards = [...selectedOtherCards, ...additional.slice(0, 50 - selectedGoldCards.length - selectedOtherCards.length)]
    }

    const selectedCards = [...selectedGoldCards, ...selectedOtherCards].slice(0, 50)

    console.log(`Mazo ${name}: ${selectedCards.length} cartas (${selectedGoldCards.length} de oro)`)

    const uniqueCards = Array.from(new Map(selectedCards.map(card => [card.id, card])).values())

    const deckCards = uniqueCards.map(card => ({
      cardId: card.id,
      card: card,
      quantity: selectedCards.filter(c => c.id === card.id).length,
    }))

    const totalCards = deckCards.reduce((sum, dc) => sum + dc.quantity, 0)

    const deck: Deck = {
      id: `demo-${i}`,
      name: name,
      description: deckDescriptions[i],
      format: formats[i % formats.length],
      isPublic: true,
      userId: 'demo',
      cards: deckCards,
      createdAt: new Date(Date.now() - i * 86400000).toISOString(),
      updatedAt: new Date().toISOString(),
    }

    console.log(`Mazo ${name} completo - ${totalCards} cartas, ${deckCards.length} únicas`)
    console.log('Primeras 5 cartas:', deckCards.slice(0, 5).map(dc => ({ name: dc.card.name, hasImage: !!(dc.card as any).localImage || dc.card.imageUrl })))

    return deck
  })

  localStorage.setItem(STORAGE_KEY, JSON.stringify(decks))
  return decks
}

interface GroupedCards {
  goldCards: Card[]
  allies: Card[]
  support: Card[]
  other: Card[]
}

function groupCardsByType(cards: Card[]): GroupedCards {
  return cards.reduce((acc, card) => {
    if (card.goldCost && card.goldCost > 0) {
      acc.goldCards.push(card)
    } else if (card.type === 'UNIT') {
      acc.allies.push(card)
    } else if (card.type === 'ARTIFACT' || card.type === 'SPELL') {
      acc.support.push(card)
    } else {
      acc.other.push(card)
    }
    return acc
  }, { goldCards: [], allies: [], support: [], other: [] } as GroupedCards)
}

function CardSection({ title, cards, color }: { title: string; cards: Card[]; color: string }) {
  if (cards.length === 0) return null

  return (
    <div className="mb-6">
      <h3 className={`text-lg font-semibold mb-3 ${color} flex items-center gap-2`}>
        {title}
        <span className="text-sm text-slate-400">({cards.length})</span>
      </h3>
      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
        {cards.map((card, i) => (
          <div key={`${card.id}-${i}`} className="aspect-[2/3]">
            <CardImage card={card} className="w-full h-full object-cover rounded hover:scale-105 transition-transform cursor-pointer" />
          </div>
        ))}
      </div>
    </div>
  )
}

function GoldCurve({ deckCards }: { deckCards: Card[] }) {
  const curve = deckCards.reduce((acc, card) => {
    const goldCost = card.goldCost || 0
    acc[goldCost] = (acc[goldCost] || 0) + 1
    return acc
  }, {} as Record<number, number>)

  const maxCount = Math.max(...Object.values(curve), 1)

  return (
    <div className="flex items-end justify-between gap-1 h-20 px-2">
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(cost => {
        const count = curve[cost] || 0
        const height = count > 0 ? (count / maxCount) * 100 : 5
        return (
          <div key={cost} className="flex-1 flex flex-col items-center">
            <div
              className={`w-full rounded-t transition-all ${count > 0 ? 'bg-amber-500' : 'bg-[#2d3540]'}`}
              style={{ height: `${height}%` }}
            />
            <span className="text-[10px] text-slate-400 mt-1">{cost}</span>
          </div>
        )
      })}
    </div>
  )
}

export default function DeckDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const storeCards = useAppStore((state) => state.cards)
  const cards = Array.isArray(storeCards) ? storeCards : []
  const allDecks = useAppStore((state) => state.decks)

  const demoDecks = useMemo(() => createRandomDecks(cards), [cards])

  const allDecksWithDemo = [...allDecks, ...demoDecks]

  const deck = useMemo(() => {
    if (!id) return null
    return allDecksWithDemo.find(d => d.id === id) || null
  }, [id, allDecksWithDemo])

  const [selectedCard, setSelectedCard] = useState<Card | null>(null)

  if (!deck) {
    return (
      <div className="min-h-screen bg-[#0f1115] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400">Mazo no encontrado</p>
          <button
            onClick={() => navigate('/decks')}
            className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-[#0f1115] font-medium rounded-lg"
          >
            Volver a Mazos
          </button>
        </div>
      </div>
    )
  }

  const deckCards = getDeckCards(deck, cards)
  const groupedCards = groupCardsByType(deckCards)

  const goldCount = deckCards.filter(c => c.goldCost && c.goldCost > 0).length
  const avgCost = deckCards.length > 0
    ? (deckCards.reduce((sum, c) => sum + c.cost, 0) / deckCards.length).toFixed(1)
    : '0.0'
  const avgGoldCost = deckCards.length > 0 && goldCount > 0
    ? deckCards.reduce((sum, c) => sum + (c.goldCost || 0), 0) / goldCount
    : 0

  const costDistribution = deckCards.reduce((acc, card) => {
    acc[card.cost] = (acc[card.cost] || 0) + 1
    return acc
  }, {} as Record<number, number>)

  return (
    <div className="min-h-screen bg-[#0f1115]">
      <div className="sticky top-0 z-40 bg-[#161920]/95 backdrop-blur-md border-b border-[#232730]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-slate-400 hover:text-white hover:bg-[#2d3540] rounded-lg transition-all"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">{deck.name}</h1>
                <p className="text-sm text-slate-400">{deck.format}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/deck/${deck.id}/edit`)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-[#0f1115] font-medium rounded-lg transition-all"
              >
                <Edit size={18} />
                Editar
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#2d3540] hover:bg-[#3d444d] text-white rounded-lg transition-all">
                <Download size={18} />
                Exportar
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#2d3540] hover:bg-[#3d444d] text-white rounded-lg transition-all">
                <Share2 size={18} />
                Compartir
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {deck.description && (
          <div className="mb-8 p-4 bg-[#1a1e24] rounded-xl border border-[#2d3540]">
            <p className="text-slate-300">{deck.description}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <CardSection title="ORO" cards={groupedCards.goldCards} color="text-amber-400" />
            <CardSection title="ALIADOS" cards={groupedCards.allies} color="text-blue-400" />
            <CardSection title="APOYO" cards={groupedCards.support} color="text-green-400" />
            {groupedCards.other.length > 0 && (
              <CardSection title="OTROS" cards={groupedCards.other} color="text-slate-400" />
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-[#1a1e24] rounded-xl p-6 border border-[#2d3540]">
              <h3 className="text-lg font-semibold text-white mb-4">Estadísticas</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total cartas</span>
                  <span className="text-white font-semibold">{deckCards.length}/50</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Cartas de oro</span>
                  <span className="text-amber-400 font-semibold">{goldCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Costo promedio</span>
                  <span className="text-white font-semibold">{avgCost}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Oro promedio</span>
                  <span className="text-amber-400 font-semibold">{avgGoldCost.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="bg-[#1a1e24] rounded-xl p-6 border border-[#2d3540]">
              <h3 className="text-lg font-semibold text-white mb-4">Curva de Oro</h3>
              <GoldCurve deckCards={deckCards} />
            </div>

            <div className="bg-[#1a1e24] rounded-xl p-6 border border-[#2d3540]">
              <h3 className="text-lg font-semibold text-white mb-4">Curva de Costo</h3>
              <div className="grid grid-cols-5 gap-2">
                {Array.from({ length: 10 }, (_, i) => i + 1).map(cost => {
                  const count = costDistribution[cost] || 0
                  return (
                    <div key={cost} className="text-center p-2 bg-[#0f1115] rounded-lg">
                      <div className="text-lg font-bold text-white">{count}</div>
                      <div className="text-xs text-slate-400">{cost}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedCard && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setSelectedCard(null)}
        >
          <div className="w-full max-w-[400px]">
            <div className="relative">
              <button
                onClick={() => setSelectedCard(null)}
                className="absolute -top-12 right-0 p-2 text-slate-400 hover:text-white"
              >
                <X size={24} />
              </button>
              <CardImage card={selectedCard} className="w-full h-auto object-contain rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
